import { useCallback, useState } from 'react'

/**
 * Theme-Umschaltung Hell / Dunkel / System (ADR-061). Die Wahl ist eine
 * GERÄTE-lokale Präferenz (localStorage, kein Server) — jede Person, auch
 * Kinder, stellt ihr eigenes Gerät ein. 'system' folgt prefers-color-scheme.
 *
 * Angewandt wird sie über das Attribut `data-theme` auf <html>:
 *  - 'light' / 'dark' → Attribut gesetzt, übersteuert das System (siehe tokens.css)
 *  - 'system'         → Attribut entfernt, CSS-@media greift
 *
 * Das Erstsetzen VOR dem ersten Paint passiert per Inline-Skript in index.html
 * (kein Flackern); dieses Modul hält die Wahl danach konsistent.
 */
export type ThemeChoice = 'system' | 'light' | 'dark'

const STORAGE_KEY = 'fn-theme'

export function getThemeChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch {
    /* localStorage nicht verfügbar (Private Mode o. Ä.) → Default */
  }
  return 'system'
}

export function applyThemeChoice(choice: ThemeChoice): void {
  const el = document.documentElement
  if (choice === 'system') el.removeAttribute('data-theme')
  else el.setAttribute('data-theme', choice)
}

export function setThemeChoice(choice: ThemeChoice): void {
  try {
    localStorage.setItem(STORAGE_KEY, choice)
  } catch {
    /* ignorieren — Persistenz optional, Anwendung erfolgt trotzdem */
  }
  applyThemeChoice(choice)
}

/** React-Hook: aktuelle Wahl + Setter (persistiert + wendet sofort an). */
export function useTheme(): readonly [ThemeChoice, (choice: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(() => getThemeChoice())
  const update = useCallback((next: ThemeChoice) => {
    setThemeChoice(next)
    setChoice(next)
  }, [])
  return [choice, update] as const
}
